import type { PmsCriticality, PmsJobRecord, PmsJobDueReason } from '../../core/pms/_models'

export type PlannedTrigger = 'date' | 'counter' | 'hybrid' | 'unknown'
export type HybridTrigger = 'date' | 'counter' | 'unknown'

export const JOB_DUE_REASON_LABELS: Record<PmsJobDueReason, string> = {
  DEFECT: 'Defect',
  BREAKDOWN: 'Breakdown',
  SAFETY_CONCERN: 'Safety Concern',
  OTHER: 'Other',
}

export const JOB_CRITICALITY_LABELS: Record<PmsCriticality, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
}

const PLANNED_TRIGGER_DESCRIPTIONS: Record<PlannedTrigger, string> = {
  date: 'Triggered by due date',
  counter: 'Triggered by running hours',
  hybrid: 'Triggered by date or counter',
  unknown: 'Scheduled maintenance',
}

export const formatJobDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-'
  try {
    const date = new Date(dateStr)
    const day = date.getDate()
    const month = date.toLocaleString('en-US', { month: 'short' })
    const year = date.getFullYear()
    return `${day} ${month} ${year}`
  } catch {
    return dateStr
  }
}

export const getPlannedTriggerLabel = (record: PmsJobRecord): PlannedTrigger => {
  if (
    record.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
    record.scheduleType === 'HYBRID_BOTH_REQUIRED'
  ) {
    return 'hybrid'
  }
  if (record.dueDate) return 'date'
  if (record.dueCounter) return 'counter'
  return 'unknown'
}

export const determineHybridTrigger = (record: PmsJobRecord): HybridTrigger => {
  if (!record.dueDate || !record.dueCounter) return 'unknown'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDate = new Date(record.dueDate)
  dueDate.setHours(0, 0, 0, 0)
  const dateCrossed = today >= dueDate
  const msPerDay = 24 * 60 * 60 * 1000
  const daysUntilDueDate = Math.ceil((dueDate.getTime() - today.getTime()) / msPerDay)

  if (!dateCrossed && daysUntilDueDate >= 0 && daysUntilDueDate <= 30) {
    return 'date'
  }

  if (record.createdAt) {
    const createdDate = new Date(record.createdAt)
    createdDate.setHours(0, 0, 0, 0)
    const daysSinceCreation = Math.floor(
      (today.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
    )
    if (daysSinceCreation <= 2 && today < dueDate) {
      return 'counter'
    }
    if (dateCrossed) {
      return 'date'
    }
  }

  return dateCrossed ? 'date' : 'counter'
}

export const getPlannedJobReasonLabel = (record: PmsJobRecord): string => {
  if (record.dueDate && record.dueCounter) {
    const hybridTrigger = determineHybridTrigger(record)
    if (hybridTrigger === 'date' && record.dueDate) {
      return `Due by Date (${formatJobDate(record.dueDate)})`
    }
    if (hybridTrigger === 'counter' && record.dueCounter) {
      return `Due by Counter (${record.dueCounter.toLocaleString()} hrs)`
    }
  }

  if (record.dueDate) {
    return `Due by Date (${formatJobDate(record.dueDate)})`
  }
  if (record.dueCounter) {
    return `Due by Counter (${record.dueCounter.toLocaleString()} hrs)`
  }
  return 'Scheduled job'
}

export const getPlannedTriggerDescription = (record: PmsJobRecord): string => {
  if (record.dueDate && record.dueCounter) {
    const hybridTrigger = determineHybridTrigger(record)
    if (hybridTrigger === 'date') return 'Triggered by due date'
    if (hybridTrigger === 'counter') return 'Triggered by running hours'
  }

  const triggerLabel = getPlannedTriggerLabel(record)
  return PLANNED_TRIGGER_DESCRIPTIONS[triggerLabel]
}

export const getUnplannedJobReasonLabel = (record: PmsJobRecord): string => {
  if (record.jobDueReason) {
    if (record.jobDueReason === 'DEFECT') {
      return 'Unplanned · From Defect'
    }
    return `Unplanned · ${JOB_DUE_REASON_LABELS[record.jobDueReason]}`
  }
  return 'Unplanned · Other'
}

export const getUnplannedJobReasonMeta = (record: PmsJobRecord): string => {
  if (record.jobDueReason === 'DEFECT' && record.defectNumber) {
    return `Linked to defect ${record.defectNumber}`
  }
  if (record.jobDueReason && record.jobDueReason !== 'DEFECT') {
    return `${JOB_DUE_REASON_LABELS[record.jobDueReason]} job`
  }
  return 'Created manually'
}

export const getJobDueReasonLabel = (record: PmsJobRecord): string =>
  record.isBreakdown ? getUnplannedJobReasonLabel(record) : getPlannedJobReasonLabel(record)

export const getJobDueReasonMetaText = (record: PmsJobRecord): string =>
  record.isBreakdown ? getUnplannedJobReasonMeta(record) : getPlannedTriggerDescription(record)

export const getJobDueReasonBadgeColor = (record: PmsJobRecord): string => {
  if (record.isBreakdown) {
    if (record.jobDueReason && JOB_DUE_REASON_LABELS[record.jobDueReason]) {
      return `badge-light-${record.jobDueReason === 'DEFECT' ? 'warning' : 'secondary'}`
    }
    return 'badge-light-danger'
  }
  return 'badge-light-success'
}

type JobStateViewLike = {
  state?: string | null
  masked?: boolean | null
  dueDate?: string | null
}

export const isMaskedJob = (record?: JobStateViewLike | null): boolean =>
  Boolean(record?.masked)

export const getDisplayedJobState = (record: JobStateViewLike): string => {
  if (Boolean(record?.masked)) return 'SCHEDULED'
  if (record.state === 'SCHEDULED' && record.dueDate) {
    const today = new Date().toISOString().slice(0, 10)
    if (record.dueDate < today) {
      return 'OVERDUE'
    }
  }
  return record.state || '-'
}

export const getDisplayedJobStateBadgeColor = (record: JobStateViewLike): string => {
  const displayedState = getDisplayedJobState(record)
  switch (displayedState) {
    case 'SCHEDULED':
      return 'badge-light-success'
    case 'OVERDUE':
      return 'badge-light-danger'
    case 'IN_PROGRESS':
      return 'badge-light-warning'
    case 'COMPLETED':
      return 'badge-light-primary'
    case 'VERIFIED':
      return 'badge-light-success'
    case 'CLOSED':
      return 'badge-light-success'
    case 'DEFERRED':
      return 'badge-light-danger'
    case 'CANCELLED':
      return 'badge-light-danger'
    default:
      return 'badge-light'
  }
}

export const getJobCriticalityLabel = (criticality?: PmsCriticality | null): string =>
  criticality ? JOB_CRITICALITY_LABELS[criticality] ?? criticality : '-'

export const getJobCriticalityBadgeColor = (criticality?: PmsCriticality | null): string => {
  switch (criticality) {
    case 'CRITICAL':
      return 'badge-light-danger'
    case 'HIGH':
      return 'badge-light-warning'
    case 'MEDIUM':
      return 'badge-light-info'
    case 'LOW':
      return 'badge-light-secondary'
    default:
      return 'badge-light'
  }
}

export const getJobNatureLabel = (record: {
  jobNature?: string | null
  planLineId?: number | null
  isBreakdown?: boolean | null
}): string => {
  if (record.jobNature === 'RECURRING' || record.planLineId) return 'Recurring'
  if (record.jobNature === 'UNPLANNED' || record.isBreakdown) return 'Unplanned'
  if (record.jobNature === 'SINGLE_TIME') return 'Single-Time'
  return 'Single-Time'
}

export const getJobNatureBadgeColor = (record: {
  jobNature?: string | null
  planLineId?: number | null
  isBreakdown?: boolean | null
}): string => {
  if (record.jobNature === 'RECURRING' || record.planLineId) return 'badge-light-primary'
  if (record.jobNature === 'UNPLANNED' || record.isBreakdown) return 'badge-light-warning'
  return 'badge-light-info'
}
